/*
  Opens the cart drawer the moment "Add to cart" is clicked, rather than waiting for
  /cart/add and every third-party cart script to finish. A placeholder shaped like the
  real cart items holds the space until the theme renders the actual contents.

  The click listener is registered on document in the CAPTURE phase deliberately: the
  upsell app binds its own capturing listener to the button and calls
  stopImmediatePropagation(), so anything bound lower in the tree never runs.

  Every element in the placeholder is a <span>: base.css hides `div:empty`.
*/
if (!window.instantCartDrawer) {
  window.instantCartDrawer = true;

  (() => {
    const LOADING_CLASS = 'cart-drawer--loading';
    const SAFETY_TIMEOUT = 15000;
    const MAX_ROWS = 4;
    let safetyTimer = null;

    const getDrawer = () => document.querySelector('cart-drawer');

    function getAddButton(target) {
      const button = target.closest?.('[name="add"]');
      if (!button || !button.closest('product-form')) return null;
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return null;
      return button;
    }

    const imageUrl = (img) => (img && (img.currentSrc || img.src)) || null;

    // Walk up a few levels from a checked option to find the card holding its thumbnail.
    function nearbyImage(element, maxDepth = 4) {
      let node = element.parentElement;
      for (let depth = 0; node && depth < maxDepth; depth += 1, node = node.parentElement) {
        const found = imageUrl(node.querySelector('img'));
        if (found) return found;
      }
      return null;
    }

    // The lines we expect to land in the cart: the product itself, plus any pre-ticked
    // upsell offers. Thumbnails are best-effort — a miss just renders a plain block.
    function previewRows(button) {
      // A button inside a product card adds only that card's product. Card grids can sit
      // in the same section as the upsell widget (complementary products do), so the card
      // — not the section — is what scopes these rows.
      const card = button.closest('.card-wrapper, .grid__item, .product-card, quick-add-modal');
      const scope =
        card ||
        button.closest('.shopify-section, [id^="shopify-section"]') ||
        document;

      const rows = [
        imageUrl(scope.querySelector('.product__media img, .card__media img, img'))
      ];

      if (card) return rows;

      // Offers render outside <product-form>, so they are matched across the page — safe
      // here because card buttons already returned above.
      document.querySelectorAll('input[type="checkbox"]:checked').forEach((box) => {
        if (rows.length >= MAX_ROWS) return;
        if (!box.offsetParent) return; // not visible to the shopper
        const thumbnail = nearbyImage(box);
        if (thumbnail) rows.push(thumbnail);
      });

      return rows;
    }

    function buildPlaceholder(rows) {
      const items = rows
        .map((thumbnail) => {
          const media = thumbnail
            ? `<span class="cart-drawer-skeleton__media" style="background-image:url(${CSS.escape(thumbnail)})"></span>`
            : '<span class="cart-drawer-skeleton__media"></span>';

          return (
            '<li class="cart-drawer-skeleton__item">' +
              media +
              '<span class="cart-drawer-skeleton__info">' +
                '<span class="cart-drawer-skeleton__title"></span>' +
                '<span class="cart-drawer-skeleton__meta"></span>' +
                '<span class="cart-drawer-skeleton__actions">' +
                  '<span class="cart-drawer-skeleton__qty"></span>' +
                  '<span class="cart-drawer-skeleton__price"></span>' +
                '</span>' +
              '</span>' +
            '</li>'
          );
        })
        .join('');

      const placeholder = document.createElement('div');
      placeholder.className = 'cart-drawer-skeleton';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.innerHTML =
        '<span class="cart-drawer-skeleton__heading"></span>' +
        `<ul class="cart-drawer-skeleton__items" role="list">${items}</ul>` +
        '<span class="cart-drawer-skeleton__footer">' +
          '<span class="cart-drawer-skeleton__subtotal"></span>' +
          '<span class="cart-drawer-skeleton__button"></span>' +
        '</span>';
      return placeholder;
    }

    function startLoading(drawer, button) {
      const inner = drawer.querySelector('.drawer-inner');
      if (inner) {
        inner.querySelector('.cart-drawer-skeleton')?.remove();
        inner.appendChild(buildPlaceholder(previewRows(button)));
      }

      drawer.classList.add(LOADING_CLASS);
      drawer.setAttribute('aria-busy', 'true');
      drawer.open(button);

      clearTimeout(safetyTimer);
      safetyTimer = setTimeout(recover, SAFETY_TIMEOUT);
    }

    function stopLoading() {
      clearTimeout(safetyTimer);

      const drawer = getDrawer();
      if (!drawer) return;
      drawer.classList.remove(LOADING_CLASS);
      drawer.removeAttribute('aria-busy');
    }

    // If the add never completes — sold out, a network error, a third-party script that
    // throws — fetch the cart ourselves so nobody is left staring at a placeholder.
    function recover() {
      const drawer = getDrawer();
      if (!drawer || !drawer.classList.contains(LOADING_CLASS)) return;

      fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
        .then((response) => response.json())
        .then((sections) => drawer.renderContents({ sections }))
        .catch(() => drawer.close())
        .finally(stopLoading);
    }

    document.addEventListener('click', (event) => {
      const button = getAddButton(event.target);
      if (!button) return;

      const drawer = getDrawer();
      if (!drawer) return;                             // cart-notification instead of drawer
      if (button.closest('quick-add-modal')) return;   // renders once its modal has closed

      startLoading(drawer, button);
    }, { capture: true });

    customElements.whenDefined('cart-drawer').then(() => {
      const proto = customElements.get('cart-drawer').prototype;
      const renderContents = proto.renderContents;

      proto.renderContents = function (parsedState) {
        stopLoading();
        return renderContents.call(this, parsedState);
      };
    });
  })();
}
